import styled from 'styled-components';

export interface IDirectionProps {
  $isRtl: boolean;
}

export interface IActiveProps {
  $isActive?: boolean;
}

export interface ISyncStatusProps {
  $status: 'synced' | 'not_synced' | 'failed' | 'disconnected';
}

// Main content wrapper
export const ManagePageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Card container for account info and table
export const ManagePageCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

// Header styles
export const ManageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
`;

export const ManageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ManageHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ManageHeaderTitle = styled.h1`
  font-weight: 700;
  font-size: 20px;
  color: #262626;
  margin: 0;
  line-height: 28px;
`;

export const ManageHeaderDescription = styled.p`
  font-weight: 400;
  font-size: 16px;
  color: #666666;
  margin: 0;
  line-height: 24px;
`;

export const ChannelLogoLarge = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg, img {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`;

export const BackArrow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  color: #697586;
  
  [dir="rtl"] & {
    transform: rotate(180deg);
  }
  
  &:hover {
    color: #5b3fff;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const AddAccountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #5b3fff;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #4a2fe6;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Account Tabs
export const AccountTabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AccountTab = styled.button<IActiveProps>`
  padding: 8px 12px;
  background: ${(props) => (props.$isActive ? '#eeebff' : 'transparent')};
  color: ${(props) => (props.$isActive ? '#1f1f1f' : '#697586')};
  border: none;
  border-radius: 6px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$isActive ? '#eeebff' : '#f6f6f8')};
  }
`;

// Disconnected Account Alert
export const DisconnectedAlertContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 17px;
  background: rgba(91, 47, 249, 0.1);
  border: 1px solid #b0a2ff;
  border-radius: 14px;
  direction: rtl;
`;

export const DisconnectedAlertText = styled.p`
  margin: 0;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #5b2ff9;
  flex: 1;
`;

export const DisconnectedAlertIcon = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: #5b2ff9;
  }
`;

// Account Info Bar
export const AccountInfoBarContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 16px;
`;

export const AccountInfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const AccountName = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #262626;
  margin: 0;
`;

export const ConnectionStatusBadge = styled.div<{ $isConnected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => (props.$isConnected ? '#ecfdf3' : '#fef3f2')};
  color: ${(props) => (props.$isConnected ? '#027a48' : '#b42318')};
`;

export const LastSyncText = styled.span`
  font-size: 14px;
  color: #697586;
`;

export const AccountInfoRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${(props) => (props.$variant === 'primary' ? '#5b3fff' : 'white')};
  color: ${(props) => (props.$variant === 'primary' ? 'white' : '#344054')};
  border: ${(props) => (props.$variant === 'primary' ? 'none' : '1px solid #d0d5dd')};
  border-radius: 999px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$variant === 'primary' ? '#4a2fe6' : '#f9fafb')};
  }

  &:disabled {
    background: white;
    color: #cccccc;
    border-color: #f0f0f0;
    cursor: not-allowed;
    
    svg {
      color: #cccccc;
      stroke: #cccccc;
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ReconnectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 17px;
  background: white;
  color: #5b2ff9;
  border: 1px solid #5b2ff9;
  border-radius: 20px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f3ff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }

  &:disabled {
    background: white;
    border-color: #f0f0f0;
    cursor: not-allowed;
    
    svg {
      color: #cccccc;
      stroke: #cccccc;
    }
  }

  svg {
    width: 20px;
    height: 20px;
    color: #697586;
  }
`;

// Table styles
export const TableContainer = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e6e6e6;
`;

export const TableHeader = styled.div`
  margin-bottom: 24px;
  padding: 24px;
`;

export const TableTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: #262626;
  margin: 0;
`;

export const UnitsCount = styled.span`
  font-size: 14px;
  color: #697586;
  background: #f2f4f7;
  padding: 4px 12px;
  border-radius: 12px;
  margin-left: 12px;
`;

export const TableFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  width: 80%;
  svg {
    position: absolute;
    top: 50%;
    inset-inline-start: 12px;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    color: #9ca3af;
  }
`;

export const TableSearchInput = styled.input`
  width: 100%;
  padding: 10px 16px;
  padding-inline-start: 40px;
  padding-inline-end: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #5b3fff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const StatusFilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  background: white;
  width: 20%;
  &:focus {
    border-color: #5b3fff;
  }
`;

// Table
export const UnitsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background: #fafafa;
`;

export const TableHeadRow = styled.tr``;

export const TableHeadCell = styled.th`
  padding: 12px 16px;
  font-weight: 500;
  font-size: 12px;
  color: #697586;
  text-align: start;
  border-bottom: 1px solid #e9e9e7;
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
  &:hover {
    background: #f9fafb;
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #262626;
  text-align: start;
  border-bottom: 1px solid #f2f4f7;
  vertical-align: middle;
`;

export const UnitImageCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const UnitImage = styled.div`
  width: 63px;
  height: 42px;
  background: #f2f4f7;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const UnitName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: #262626;
`;

// Sync Status Badge
export const SyncStatusBadge = styled.div<ISyncStatusProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$status) {
      case 'synced':
        return '#ecfdf3';
      case 'not_synced':
        return '#fffaeb';
      case 'failed':
        return '#fef3f2';
      case 'disconnected':
        return '#fef3f2';
      default:
        return '#f2f4f7';
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case 'synced':
        return '#027a48';
      case 'not_synced':
        return '#b54708';
      case 'failed':
        return '#b42318';
      case 'disconnected':
        return '#b42318';
      default:
        return '#344054';
    }
  }};
`;

export const SyncStatusDot = styled.div<ISyncStatusProps>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.$status) {
      case 'synced':
        return '#12b76a';
      case 'not_synced':
        return '#f79009';
      case 'failed':
        return '#f04438';
      case 'disconnected':
        return '#f04438';
      default:
        return '#667085';
    }
  }};
`;

// Action Menu
export const ActionMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  color: #697586;

  &:hover {
    background: #f2f4f7;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Empty state
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f2f4f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    width: 32px;
    height: 32px;
    color: #697586;
  }
`;

export const EmptyStateTitle = styled.h4`
  font-weight: 600;
  font-size: 16px;
  color: #262626;
  margin: 0 0 8px;
`;

export const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: #697586;
  margin: 0;
`;

// Loading skeleton
export const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #f2f4f7;
`;

export const SkeletonCell = styled.div<{ $width?: string }>`
  height: 20px;
  width: ${(props) => props.$width || '100px'};
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export const SkeletonImage = styled.div`
  width: 63px;
  height: 42px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
`;

// Page-level skeleton for header
export const HeaderSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
`;

export const HeaderSkeletonLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const SkeletonLogo = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;

export const SkeletonTitle = styled.div`
  width: 200px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 8px;
`;

export const SkeletonDescription = styled.div`
  width: 300px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;

export const SkeletonButton = styled.div`
  width: 180px;
  height: 44px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;

export const SkeletonTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

export const SkeletonTab = styled.div`
  width: 120px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;

export const SkeletonInfoBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e9e9e7;
`;

export const SkeletonInfoBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const SkeletonBadge = styled.div`
  width: 80px;
  height: 24px;
  border-radius: 20px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;

export const SkeletonInfoBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SkeletonActionButton = styled.div`
  width: 140px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9e9e7 50%, #f2f4f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;
